import { CasesService, type GenericDataMapper } from '@/types/generated'
import { defineStore } from 'pinia'
import { ref } from 'vue'

/**
 * Holds the org-wide data mapper schema. Identical for every case, fetched
 * once from `/cases/generic_datamappers/` and shared with all consumers.
 */
export const useDataMapperStore = defineStore('dataMapperStore', () => {
  const mappers = ref<GenericDataMapper[]>([])
  const isLoading = ref(false)
  const hasLoaded = ref(false)
  const loadError = ref<Error | null>(null)
  let inFlight: Promise<GenericDataMapper[]> | null = null

  async function fetchMappers(options?: { force?: boolean }): Promise<GenericDataMapper[]> {
    if (!options?.force && hasLoaded.value) return mappers.value
    if (!options?.force && inFlight) return inFlight

    isLoading.value = true
    loadError.value = null
    inFlight = (async () => {
      try {
        const response = await CasesService.casesGenericDatamappersList()
        const results = (response.results ?? []) as GenericDataMapper[]
        mappers.value = results
        hasLoaded.value = true
        return results
      } catch (error) {
        loadError.value = error instanceof Error ? error : new Error(String(error))
        throw error
      } finally {
        isLoading.value = false
        inFlight = null
      }
    })()

    return inFlight
  }

  function getMapperByKey(key: string): GenericDataMapper | undefined {
    return mappers.value.find((m) => m.key === key)
  }

  return {
    mappers,
    isLoading,
    hasLoaded,
    loadError,
    fetchMappers,
    getMapperByKey
  }
})
